/**
 * HotspotComponent - Interactive 3D hotspots for VR experiences
 * Supports click/hover interactions with audio and info display
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';

const logger = Logger.getLogger('HotspotComponent');
import '@babylonjs/core/Animations/animatable'; // Augment Scene with beginAnimation
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Skeleton } from '@babylonjs/core/Bones/skeleton';
import { Bone } from '@babylonjs/core/Bones/bone';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Animation } from '@babylonjs/core/Animations/animation';
import { ActionManager } from '@babylonjs/core/Actions/actionManager';
import { ExecuteCodeAction } from '@babylonjs/core/Actions/directActions';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Analyser } from '@babylonjs/core/Audio/analyser';
import '@babylonjs/core/Meshes/mesh.vertexData';
import '@babylonjs/core/Collisions/pickingInfo';
import './HotspotShaders'; // Register custom shaders

// AudioEngineV2 imports - CreateSoundAsync for spatial audio
import { CreateSoundAsync } from '@babylonjs/core/AudioV2/abstractAudio/audioEngineV2';
import { CreateAudioEngineAsync } from '@babylonjs/core/AudioV2/webAudio/webAudioEngine';
import type { AbstractSound } from '@babylonjs/core/AudioV2/abstractAudio/abstractSound';
import type { Observer } from '@babylonjs/core/Misc/observable';
import { SoundState } from '@babylonjs/core/AudioV2/soundState';

// Infer AudioEngineV2 type from factory function return
type AudioEngineV2 = Awaited<ReturnType<typeof CreateAudioEngineAsync>>;

// Import shared HotspotConfig from centralized types
import type { HotspotConfig } from '../types';

// Re-export for backward compatibility
export type { HotspotConfig };

/**
 * Extended spatial properties for AudioV2 that may exist at runtime
 */
interface ExtendedSpatialProperties {
  refDistance?: number;
  coneInnerAngle?: number;
  coneOuterAngle?: number;
  coneOuterGain?: number;
}

/**
 * Sound with event listener support (AudioV2)
 */
interface SoundWithEvents extends AbstractSound {
  addEventListener?(event: string, callback: () => void): void;
}

// Debug info interface
interface SoundDebugInfo {
  id: string;
  name: string;
  isPlaying: boolean;
  volume: number;
  spatial: {
    enabled: boolean;
    distanceModel: string;
    maxDistance: number;
    rolloffFactor: number;
    attachedTo: string;
  } | string;
  position: { x: string; y: string; z: string } | null;
}

interface HotspotDebugInfo {
  totalHotspots: number;
  totalSounds: number;
  sounds: SoundDebugInfo[];
}

export class HotspotComponent {
  private scene: Scene;
  private parentNode: TransformNode | null = null;
  private hotspots: Map<string, Mesh> = new Map();
  private materials: Map<string, StandardMaterial | ShaderMaterial> = new Map();
  private sounds: Map<string, AbstractSound> = new Map();
  private analysers: Map<string, Analyser> = new Map();
  private animations: Map<string, Animation> = new Map();
  private renderObserver: Observer<Scene> | null = null;
  private animationObservables: Map<string, Observer<Scene>> = new Map();
  private audioEngine: AudioEngineV2 | null = null;
  private playingStates: Map<string, boolean> = new Map(); // Track playing states manually

  // Callbacks for interaction events
  public onHotspotClick?: (hotspot: HotspotConfig) => void;
  public onHotspotHover?: (hotspot: HotspotConfig) => void;
  public onHotspotHoverExit?: (hotspot: HotspotConfig) => void;
  public onAudioPlay?: (hotspot: HotspotConfig) => void;
  public onAudioStop?: (hotspot: HotspotConfig) => void;
  public onShowLeadForm?: (hotspot: HotspotConfig) => void;

  constructor(scene: Scene, audioEngine?: AudioEngineV2 | null) {
    this.scene = scene;
    this.audioEngine = audioEngine ?? null;

    // Setup render loop for shader updates
    this.renderObserver = this.scene.onBeforeRenderObservable.add(() => {
      this.updateShaders();
    });
  }

  /**
   * Set the parent node for all hotspot meshes (for skybox rotation grouping)
   */
  setParentNode(parent: TransformNode | null): void {
    this.parentNode = parent;
    // Re-parent any existing hotspot meshes
    this.hotspots.forEach((mesh) => {
      mesh.parent = parent;
    });
  }

  /**
   * Set the audio engine (called after AudioEngineV2 is created)
   */
  setAudioEngine(audioEngine: AudioEngineV2): void {
    this.audioEngine = audioEngine;
    logger.debug('AudioEngineV2 set');
  }

  /**
   * Load multiple hotspots
   * FIXED: Uses Promise.allSettled to handle individual hotspot failures gracefully
   * @param hotspotConfigs - Array of hotspot configurations
   * @param isEditor - Whether loading in editor mode (affects portal visibility)
   * @param enableInteraction - Whether to enable click/hover interactions (false for editor preview)
   */
  async load(hotspotConfigs: HotspotConfig[], isEditor: boolean = false, enableInteraction: boolean = true): Promise<void> {
    logger.info(`Loading ${hotspotConfigs.length} hotspot(s)... (editor: ${isEditor}, interaction: ${enableInteraction})`);

    // Unload any existing hotspots first
    this.unload();

    // FIXED: Use Promise.allSettled to handle individual failures gracefully
    // This ensures one failing hotspot doesn't break all others
    const loadPromises = hotspotConfigs.map((config) => this.loadSingleHotspot(config, isEditor, enableInteraction));
    const results = await Promise.allSettled(loadPromises);

    // Log any failures but continue with successfully loaded hotspots
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      logger.warn(`${failures.length} hotspot(s) failed to load:`);
      failures.forEach((f, i) => logger.warn(`  - Hotspot ${i}: ${f.reason}`));
    }

    const successCount = results.filter(r => r.status === 'fulfilled').length;
    logger.info(`${successCount}/${hotspotConfigs.length} hotspot(s) loaded successfully`);

    // Show all hotspots now that they are fully loaded and positioned
    this.showAllHotspots(isEditor);
  }

  /**
   * Show all hotspots with a fade-in effect
   * Called after all hotspots are fully loaded to prevent visual glitches
   * @param isEditor - Whether in editor mode
   */
  private showAllHotspots(isEditor: boolean): void {
    this.hotspots.forEach((hotspot, id) => {
      const config = hotspot.metadata?.config as HotspotConfig | undefined;

      // Navigation hotspots in viewer mode stay hidden (portals handle their visuals)
      // Also keep floor-arrow hotspots hidden in editor mode (FloorArrowManager handles the visual)
      if (config?.type === 'navigation' && (!isEditor || config.portalEffectStyle === 'floor-arrow')) {
        hotspot.visibility = 0;
        return;
      }

      // Fade in the hotspot
      const fadeIn = new Animation(
        `fadeIn_${id}`,
        'visibility',
        30, // 30 fps
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );

      fadeIn.setKeys([
        { frame: 0, value: 0 },
        { frame: 10, value: 1 } // ~0.33 seconds fade
      ]);

      hotspot.animations = hotspot.animations || [];
      hotspot.animations.push(fadeIn);
      this.scene.beginAnimation(hotspot, 0, 10, false);
    });

    logger.debug(`Showing ${this.hotspots.size} hotspot(s) with fade-in`);
  }

  /**
   * Load a single hotspot
   * @param config - Hotspot configuration
   * @param isEditor - Whether loading in editor mode
   * @param enableInteraction - Whether to enable click/hover interactions
   */
  private async loadSingleHotspot(config: HotspotConfig, isEditor: boolean, enableInteraction: boolean = true): Promise<void> {
    try {
      logger.debug(`Loading hotspot: ${config.id}`);

      // Create hotspot mesh (sphere)
      // Navigation hotspots need to be large (30) for portal effect
      // Info hotspots are 3x larger (24) for better visibility
      // Audio hotspots default to 8.0
      let defaultSize = 8.0;
      if (config.type === 'navigation') {
        defaultSize = 30;
      } else if (config.type === 'info') {
        defaultSize = 24; // 3x larger for info hotspots
      }
      
      const size = config.size || defaultSize;
      let hotspot: Mesh;
      
      if (config.type === 'info') {
        // Info hotspots: Parent mesh (background) + Child mesh (icon)
        // 1. Background Plane (Siri Effect)
        hotspot = MeshBuilder.CreatePlane(
            `hotspot_${config.id}`,
            {
                size: size * 2, 
                sideOrientation: Mesh.DOUBLESIDE
            },
            this.scene
        );
        hotspot.billboardMode = Mesh.BILLBOARDMODE_ALL; // Full billboard for desktop
        hotspot.renderingGroupId = 1; // Background layer
        
        // 2. Icon Plane (Child)
        const iconMesh = MeshBuilder.CreatePlane(
            `hotspot_icon_${config.id}`,
            {
                size: size * 2,
                sideOrientation: Mesh.DOUBLESIDE
            },
            this.scene
        );
        iconMesh.parent = hotspot;
        iconMesh.position.z = -0.1; // Increased separation
        iconMesh.renderingGroupId = 2; // Foreground layer (draws on top)
        iconMesh.isPickable = false; // Ensure clicks pass through to parent
        // No billboard needed on child as parent billboards
        
        // Store reference to icon mesh for material assignment
        hotspot.metadata = { iconMesh };
        
      } else if (config.type === 'audio' || config.type === 'both') {
        // Audio hotspots are billboarded planes
        // User requested 3x bigger for "play hotspots"
        const audioSize = size * 3;
        hotspot = MeshBuilder.CreatePlane(
            `hotspot_${config.id}`,
            {
                size: audioSize * 2, 
                sideOrientation: Mesh.DOUBLESIDE
            },
            this.scene
        );
        hotspot.billboardMode = Mesh.BILLBOARDMODE_ALL; // Full billboard for desktop
      } else {
        // Navigation hotspots remain spheres
        hotspot = MeshBuilder.CreateSphere(
            `hotspot_${config.id}`,
            {
            diameter: size * 2,
            segments: 16,
            },
            this.scene
        );
      }

      // Set position
      hotspot.position = new Vector3(
        config.position.x,
        config.position.y,
        config.position.z
      );

      // Start hidden - will be shown after all hotspots are fully loaded
      // This prevents visual glitches where hotspots appear before being positioned
      hotspot.visibility = 0;

      // Ensure renders on top of skybox
      hotspot.renderingGroupId = 1;

      // Prevent incorrect per-eye frustum culling for plane-based hotspots in VR stereo
      // (fixes one-eye-only rendering on VisionOS 2 with billboard planes)
      if (config.type !== 'navigation') {
        hotspot.alwaysSelectAsActiveMesh = true;
      }

      logger.debug(`Created mesh for ${config.id} at ${hotspot.position} with size ${size}`);

      // Define color and isNavigation before using them
      const color = config.color ? Color3.FromHexString(config.color) : new Color3(1, 0, 0);
      const isNavigation = config.type === 'navigation';

      // Create material based on type
      let material: StandardMaterial | ShaderMaterial;

      if (isNavigation) {
        // Navigation hotspots: Use portal color for visibility
        const portalColor = config.portalColor
          ? Color3.FromHexString(config.portalColor)
          : new Color3(0.2, 0.6, 1.0); // Default portal blue

        const stdMaterial = new StandardMaterial(`hotspot_material_${config.id}`, this.scene);

        logger.debug(`Navigation hotspot ${config.id}: isEditor=${isEditor}, portalEffectStyle=${config.portalEffectStyle}`);
        if (isEditor && config.portalEffectStyle === 'floor-arrow') {
          // Editor + floor-arrow: HIDE the sphere completely - FloorArrowManager handles the visual
          // The arrow itself will be pickable for dragging
          logger.debug(`Hiding sphere for floor-arrow ${config.id} (arrow is draggable)`);
          stdMaterial.alpha = 0;
          stdMaterial.diffuseColor = new Color3(0, 0, 0);
          stdMaterial.emissiveColor = new Color3(0, 0, 0);
        } else if (isEditor) {
          // Editor: Semi-transparent colored sphere so user can see portal location
          logger.debug(`Showing sphere for ${config.id}`);
          stdMaterial.diffuseColor = portalColor;
          stdMaterial.emissiveColor = portalColor.scale(0.5); // Gentle glow
          stdMaterial.alpha = 0.6;
          stdMaterial.wireframe = false; // Show solid, not wireframe
          stdMaterial.backFaceCulling = false;
        } else {
          // Viewer: Invisible (raycast target only, PortalPreviewManager/FloorArrowManager handles visual)
          logger.debug(`Hiding sphere for ${config.id} (viewer mode)`);
          stdMaterial.alpha = 0;
          stdMaterial.diffuseColor = new Color3(0, 0, 0);
          stdMaterial.emissiveColor = new Color3(0, 0, 0);
        }
        material = stdMaterial;
      }

      // =====================================================================
      // CUSTOM ICON CHECK - If customIconUrl is set, use texture instead of shader
      // =====================================================================
      if (config.customIconUrl && config.type !== 'navigation') {
        logger.debug(`Using custom icon for ${config.id}: ${config.customIconUrl}`);

        // Create StandardMaterial with custom texture
        const customMaterial = new StandardMaterial(`hotspot_custom_material_${config.id}`, this.scene);

        // Load custom icon texture
        const customTexture = new Texture(config.customIconUrl, this.scene, false, true, Texture.BILINEAR_SAMPLINGMODE, () => {
          logger.debug(`Custom icon loaded for ${config.id}`);
        }, (message, exception) => {
          logger.error(`Failed to load custom icon for ${config.id}:`, message, exception);
        });

        customTexture.hasAlpha = true;

        // Apply texture to material - use emissive for visibility in any lighting
        customMaterial.diffuseTexture = customTexture;
        customMaterial.emissiveTexture = customTexture;
        customMaterial.useAlphaFromDiffuseTexture = true;
        customMaterial.emissiveColor = new Color3(1, 1, 1); // Full emissive so icon is always visible
        customMaterial.backFaceCulling = false;
        customMaterial.transparencyMode = 2; // Alpha test + blend

        material = customMaterial;

        // Hide icon mesh if it exists (we're using custom icon instead)
        if (hotspot.metadata?.iconMesh) {
          hotspot.metadata.iconMesh.dispose();
          hotspot.metadata.iconMesh = null;
        }

      } else if (config.type === 'info') {
        // Info hotspot: Two shaders (default when no custom icon)

        // 1. Background Shader
        const bgMaterial = new ShaderMaterial(
          `hotspot_bg_material_${config.id}`,
          this.scene,
          {
            vertex: "infoHotspot",
            fragment: "infoHotspotBackground",
          },
          {
            attributes: ["position", "normal", "uv"],
            uniforms: ["worldViewProjection", "world", "time", "color", "hoverStrength", "cameraPosition"],
          }
        );

        bgMaterial.setColor3("color", color);
        bgMaterial.setFloat("time", 0);
        bgMaterial.setFloat("hoverStrength", 0);
        bgMaterial.backFaceCulling = false;
        bgMaterial.transparencyMode = 3; // Alpha blend

        material = bgMaterial; // Main material on parent

        // 2. Icon Shader
        if (hotspot.metadata && hotspot.metadata.iconMesh) {
            const iconMaterial = new ShaderMaterial(
              `hotspot_icon_material_${config.id}`,
              this.scene,
              {
                vertex: "infoHotspot",
                fragment: "infoHotspotIcon",
              },
              {
                attributes: ["position", "normal", "uv"],
                uniforms: ["worldViewProjection", "world", "color", "time", "hoverStrength"],
              }
            );

            iconMaterial.setColor3("color", color);
            iconMaterial.setFloat("time", 0);
            iconMaterial.setFloat("hoverStrength", 0);
            iconMaterial.backFaceCulling = false;
            iconMaterial.transparencyMode = 3;

            hotspot.metadata.iconMesh.material = iconMaterial;
        }

      } else if (config.type === 'audio' || config.type === 'both') {
        // Audio hotspot: Audio Reactive Shader
        const shaderMaterial = new ShaderMaterial(
          `hotspot_shader_${config.id}`,
          this.scene,
          {
            vertex: "audioHotspot",
            fragment: "audioHotspot",
          },
          {
            attributes: ["position", "normal", "uv"],
            uniforms: ["world", "worldViewProjection", "color", "time", "cameraPosition", "audioLevel", "hoverStrength"],
          }
        );

        shaderMaterial.setColor3("color", color);
        shaderMaterial.setFloat("time", 0);
        shaderMaterial.setFloat("audioLevel", 0);
        shaderMaterial.setFloat("hoverStrength", 0); // Initialize hover strength
        shaderMaterial.setFloat("isPlaying", 0); // 0 = paused, 1 = playing
        shaderMaterial.backFaceCulling = false;
        shaderMaterial.transparencyMode = 3; // Alpha blend

        material = shaderMaterial;
      } else {
        // Fallback
        const stdMaterial = new StandardMaterial(`hotspot_material_${config.id}`, this.scene);
        stdMaterial.diffuseColor = color;
        stdMaterial.emissiveColor = color.scale(0.6);
        stdMaterial.alpha = config.opacity !== undefined ? config.opacity : 0.9;
        material = stdMaterial;
      }

      hotspot.material = material;

      // Size
      if (config.size !== undefined) {
        hotspot.scaling = new Vector3(config.size, config.size, config.size);
      }

      // Ensure it renders on top of skybox but behind UI
      hotspot.renderingGroupId = 1;

      // Make navigation hotspots invisible in these cases:
      // 1. Viewer mode: PortalPreviewManager/FloorArrowManager handles the visual
      // 2. Editor mode + floor-arrow: FloorArrowManager handles the visual, we drag the arrow directly
      if (config.type === 'navigation' && (!isEditor || config.portalEffectStyle === 'floor-arrow')) {
        hotspot.visibility = 0;
        // Only disable picking for floor-arrow (FloorArrowManager's collision plane handles clicks)
        // Non-floor-arrow portals MUST remain pickable for click detection (PortalPreviewManager is visual-only)
        if (config.portalEffectStyle === 'floor-arrow') {
          hotspot.isPickable = false;
        }
      }
      
      // Store config for interaction
      hotspot.metadata = { config };

      // Only add action manager if interaction is enabled
      // In editor preview mode, we show hotspots visually but don't want click/hover interactions
      if (enableInteraction) {
        hotspot.actionManager = new ActionManager(this.scene);

        // Hover effects
        hotspot.actionManager.registerAction(
          new ExecuteCodeAction(
            ActionManager.OnPointerOverTrigger,
            () => {
              this.scene.hoverCursor = 'pointer';
              this.handleHotspotHover(config, hotspot);
            }
          )
        );

        hotspot.actionManager.registerAction(
          new ExecuteCodeAction(
            ActionManager.OnPointerOutTrigger,
            () => {
              this.scene.hoverCursor = 'default';
              this.handleHotspotHoverExit(config, hotspot);
            }
          )
        );

        // Click interaction
        hotspot.actionManager.registerAction(
          new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
            logger.debug(`Hotspot clicked: ${config.id}`);
            this.handleHotspotClick(config);
          })
        );
      } else {
        // Still make pickable for potential drag operations, but no click handlers
        // EXCEPT for floor-arrow hotspots - their collision plane handles picking
        if (!(config.type === 'navigation' && config.portalEffectStyle === 'floor-arrow')) {
          hotspot.isPickable = true;
        }
      }

      // DEBUG: Log final hotspot state for floor-arrows
      if (config.type === 'navigation' && config.portalEffectStyle === 'floor-arrow') {
        logger.debug(`Floor-arrow hotspot ${config.id} final state:`, {
          isPickable: hotspot.isPickable,
          visibility: hotspot.visibility,
          position: `(${hotspot.position.x.toFixed(1)}, ${hotspot.position.y.toFixed(1)}, ${hotspot.position.z.toFixed(1)})`,
        });
      }

      // Parent to scene content parent if set (for skybox rotation grouping)
      if (this.parentNode) {
        hotspot.parent = this.parentNode;
      }

      // Store references BEFORE loading audio (so audio can attach to mesh)
      this.hotspots.set(config.id, hotspot);
      this.materials.set(config.id, material);

      // Add pulse animation (more energetic for navigation hotspots)
      this.addPulseAnimation(hotspot, config.id, isNavigation);

      // Load audio in the background — don't block hotspot visibility
      // The panel and hotspot appear immediately; audio streams in while visible
      if (config.audioUrl && (config.type === 'audio' || config.type === 'both')) {
        this.loadAudio(config).then(() => {
          // Add audio title display once audio is ready
          const audioTitle = config.audioTitle || config.audioUrl!.split('/').pop()?.split('?')[0] || 'Audio';
          this.createAudioTitleDisplay(hotspot, audioTitle, config);
        }).catch((audioError) => {
          logger.error(`Failed to load audio for ${config.id}, but hotspot will still work:`, audioError);
        });
      }

    } catch (error) {
      logger.error(`Failed to load hotspot ${config.id}:`, error);
    }
  }

  /**
   * Create audio title display below hotspot
   */
  private createAudioTitleDisplay(hotspot: Mesh, title: string, config: HotspotConfig): void {
    // Create a plane for the text
    const textPlane = MeshBuilder.CreatePlane(`audio_title_${config.id}`, {
      width: 2.5,
      height: 0.5,
      sideOrientation: Mesh.DOUBLESIDE
    }, this.scene);

    // Position below the hotspot
    textPlane.parent = hotspot;
    textPlane.position.y = -0.8; // Below the hotspot
    textPlane.isPickable = false; // Don't intercept picks meant for the parent hotspot
    textPlane.billboardMode = Mesh.BILLBOARDMODE_ALL; // Always face camera (desktop), will be Y-only in VR

    // Create dynamic texture for text
    const dynamicTexture = new DynamicTexture(`audio_title_texture_${config.id}`, {
      width: 512,
      height: 128
    }, this.scene);

    // Draw text with larger font
    const font = "bold 48px Arial";
    dynamicTexture.drawText(title, null, null, font, "white", "transparent", true, true);

    // Create material with the text texture
    const textMaterial = new StandardMaterial(`audio_title_material_${config.id}`, this.scene);
    textMaterial.diffuseTexture = dynamicTexture;
    textMaterial.emissiveTexture = dynamicTexture;
    textMaterial.opacityTexture = dynamicTexture;
    textMaterial.transparencyMode = 3; // Alpha blend
    textMaterial.emissiveColor = new Color3(1, 1, 1);
    textMaterial.disableLighting = true;
    textMaterial.backFaceCulling = false;

    textPlane.material = textMaterial;

    // Store reference for later updates
    if (!hotspot.metadata) {
      hotspot.metadata = {};
    }
    hotspot.metadata.titlePlane = textPlane;
    hotspot.metadata.titleTexture = dynamicTexture;

    logger.debug(`Created audio title display: "${title}" for hotspot ${config.id}`);
  }

  /**
   * Handle hotspot click
   */
  private handleHotspotClick(config: HotspotConfig): void {
    // Check for lead capture type - show form instead of info panel
    if (config.type === 'lead_capture') {
      logger.debug(`Lead capture click: ${config.id}`);
      if (this.onShowLeadForm) {
        this.onShowLeadForm(config);
      }
      return; // Don't process further - form takes precedence
    }

    // Play audio if configured
    if (config.audioUrl && (config.type === 'audio' || config.type === 'both')) {
      this.playAudio(config.id);
    }

    // Trigger callback
    if (this.onHotspotClick) {
      this.onHotspotClick(config);
    }
  }

  /**
   * Handle hotspot hover
   */
  private handleHotspotHover(config: HotspotConfig, mesh: Mesh): void {
    // Scale up on hover
    const material = this.materials.get(config.id);
    if (material) {
      if (material instanceof StandardMaterial) {
        material.emissiveColor = material.diffuseColor.scale(1.0); // Brighten
      } else if (material instanceof ShaderMaterial) {
        // Animate hoverStrength uniform
        this.animateHoverStrength(material, 0, 1);
      }
    }

    // Scale animation
    mesh.scaling = new Vector3(1.2, 1.2, 1.2);

    // Make title more visible on hover if it exists
    if (mesh.metadata?.titlePlane) {
      const titlePlane = mesh.metadata.titlePlane;
      titlePlane.scaling = new Vector3(1.2, 1.2, 1.2);
    }

    // Trigger callback
    if (this.onHotspotHover) {
      this.onHotspotHover(config);
    }
  }

  /**
   * Handle hotspot hover exit
   */
  private handleHotspotHoverExit(config: HotspotConfig, mesh: Mesh): void {
    // Reset scale
    const material = this.materials.get(config.id);
    if (material) {
      if (material instanceof StandardMaterial) {
        const color = config.color ? this.parseColor(config.color) : new Color3(1, 0, 0);
        material.emissiveColor = color.scale(0.6); // Reset glow
      } else if (material instanceof ShaderMaterial) {
        // Animate hoverStrength uniform back to 0
        this.animateHoverStrength(material, 1, 0);
      }
    }
    
    // Reset pulse animation speed if needed
    const isNavigation = config.type === 'navigation';
    if (isNavigation) {
        // Keep pulsing
    }

    mesh.scaling = new Vector3(1, 1, 1);

    // Reset title scale if it exists
    if (mesh.metadata?.titlePlane) {
      const titlePlane = mesh.metadata.titlePlane;
      titlePlane.scaling = new Vector3(1, 1, 1);
    }

    // Trigger callback
    if (this.onHotspotHoverExit) {
      this.onHotspotHoverExit(config);
    }
  }

  /**
   * Reset hover state for a hotspot (used when closing popups on mobile where no pointerOut fires)
   */
  public resetHoverState(id: string): void {
    const hotspot = this.hotspots.get(id);
    const config = hotspot?.metadata?.config as HotspotConfig | undefined;
    if (hotspot && config) {
      // Only reset if hover is actually active (scaling > 1 indicates hover)
      if (hotspot.scaling.x > 1) {
        this.handleHotspotHoverExit(config, hotspot);
      }
    }
  }

  /**
   * Add pulsing animation to hotspot
   */
  private addPulseAnimation(mesh: Mesh, id: string, isNavigation: boolean = false): void {
    const animation = new Animation(
      `hotspot_pulse_${id}`,
      'scaling',
      30,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );

    // Navigation hotspots pulse more dramatically
    const maxScale = isNavigation ? 1.15 : 1.1;

    const keyFrames = [
      { frame: 0, value: new Vector3(1, 1, 1) },
      { frame: 30, value: new Vector3(maxScale, maxScale, maxScale) },
      { frame: 60, value: new Vector3(1, 1, 1) },
    ];

    animation.setKeys(keyFrames);
    mesh.animations.push(animation);

    // Navigation hotspots pulse faster
    const speed = isNavigation ? 1.2 : 1.0;
    this.scene.beginAnimation(mesh, 0, 60, true, speed);

    this.animations.set(id, animation);
  }

  /**
   * Animate hover strength uniform
   */
  private animateHoverStrength(material: ShaderMaterial, from: number, to: number): void {
    const animation = new Animation(
      "hoverAnimation",
      "hoverStrength", // This won't work directly on material, need to use a property or manual update
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const keyFrames = [
      { frame: 0, value: from },
      { frame: 20, value: to },
    ];

    animation.setKeys(keyFrames);
    
    // We need to animate a proxy object or handle this manually since uniforms aren't direct properties
    // Simplest way: just use a manual tween in the render loop or a temporary object
    // Let's use a custom animation loop for this specific property
    
    // Cancel any existing animation for this material
    const existingObs = this.animationObservables.get(material.uniqueId.toString());
    if (existingObs) {
        this.scene.onBeforeRenderObservable.remove(existingObs);
        this.animationObservables.delete(material.uniqueId.toString());
    }
    
    let currentFrame = 0;
    const totalFrames = 20;
    
    const obs = this.scene.onBeforeRenderObservable.add(() => {
        currentFrame++;
        const progress = Math.min(currentFrame / totalFrames, 1.0);
        // Ease out cubic
        const ease = 1 - Math.pow(1 - progress, 3);
        const value = from + (to - from) * ease;
        
        material.setFloat("hoverStrength", value);
        
        if (progress >= 1.0) {
            this.scene.onBeforeRenderObservable.remove(obs);
            this.animationObservables.delete(material.uniqueId.toString());
        }
    });
    
    this.animationObservables.set(material.uniqueId.toString(), obs);
  }

  /**
   * Load audio for hotspot using new AudioEngineV2 API
   */
  private async loadAudio(config: HotspotConfig): Promise<void> {
    if (!config.audioUrl) return;

    // Check if audio engine is available
    if (!this.audioEngine) {
      logger.warn(`AudioEngineV2 not available, skipping audio for ${config.id}`);
      return;
    }

    try {
        const audioUrl = config.audioUrl!;
        const hotspotId = config.id;

        logger.debug(`Loading SPATIAL audio for ${hotspotId}`);
        logger.debug(`Engine state: ${this.audioEngine.state}`);
        logger.debug(`Audio URL: ${audioUrl}`);

        // ALWAYS use CreateSoundAsync for spatial audio support
        // Streaming sounds don't support spatial positioning!
        logger.debug('Using CreateSoundAsync with spatial audio enabled');

        const sound = await CreateSoundAsync(
          `hotspot_audio_${hotspotId}`,
          audioUrl,
          {
            loop: config.audioLoop || false,
            autoplay: false,
            maxInstances: 1,
            volume: config.audioVolume ?? 1.0,
            spatialEnabled: true, // CRITICAL: Enable spatial audio
            spatialDistanceModel: "linear", // Linear distance falloff
            spatialMaxDistance: 600, // Match dome radius (~500 units + margin)
            spatialRolloffFactor: 0.5, // Gentler falloff for large distances with distance
            spatialConeInnerAngle: 360, // Full omnidirectional
            spatialConeOuterAngle: 360, // Full omnidirectional
            spatialConeOuterGain: 1 // No reduction outside cone
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any, // AudioV2 API has complex overloads - explicit any required
          this.audioEngine // Pass the audio engine instance
        );

        logger.debug(`Audio created for ${hotspotId}`);
        logger.debug(`Sound object: ${sound}`);
        logger.debug(`Sound type: ${sound.constructor.name}`);

        // Attach spatial audio to the hotspot mesh
        const hotspotMesh = this.hotspots.get(hotspotId);
        if (hotspotMesh && sound.spatial) {
          // First attach to the mesh
          sound.spatial.attach(hotspotMesh);
          logger.debug(`Spatial audio attached to mesh ${hotspotId}`);
          logger.debug(`Mesh position: ${hotspotMesh.position}`);

          // NOW set the spatial properties directly on the spatial object
          // This ensures they override any defaults

          // IMPORTANT: Set maxDistance to match dome radius (~500 units)
          // since hotspots are positioned on the dome surface!
          sound.spatial.distanceModel = "linear";
          sound.spatial.maxDistance = 600;  // Increased to cover dome radius + margin
          sound.spatial.rolloffFactor = 0.5;  // Gentler falloff for large distances

          // If these properties exist, set them too (extended spatial properties)
          const spatialExt = sound.spatial as typeof sound.spatial & ExtendedSpatialProperties;
          if ('refDistance' in spatialExt) {
            spatialExt.refDistance = 1;
          }
          if ('coneInnerAngle' in spatialExt) {
            spatialExt.coneInnerAngle = 360;
            spatialExt.coneOuterAngle = 360;
            spatialExt.coneOuterGain = 1;
          }

          logger.debug('Set spatial properties directly');

          // Log spatial properties to verify they're set correctly
          logger.debug('Spatial properties after setting:', {
            distanceModel: sound.spatial.distanceModel,
            maxDistance: sound.spatial.maxDistance,
            rolloffFactor: sound.spatial.rolloffFactor,
            attachedTo: hotspotMesh.name,
            meshPosition: `${hotspotMesh.position.x.toFixed(2)}, ${hotspotMesh.position.y.toFixed(2)}, ${hotspotMesh.position.z.toFixed(2)}`
          });
        } else if (!sound.spatial) {
          logger.error(`Spatial audio NOT available for ${hotspotId}! This should not happen!`);
        }

        // Handle audio end event - AudioEngineV2 uses addEventListener
        const soundWithEvents = sound as SoundWithEvents;
        if (soundWithEvents.addEventListener) {
          soundWithEvents.addEventListener('ended', () => {
            logger.debug(`Audio ended for ${hotspotId}`);
            this.playingStates.set(hotspotId, false);
            const material = this.materials.get(hotspotId);
            if (material && material instanceof ShaderMaterial) {
              material.setFloat("isPlaying", 0);
            }
          });
        }

        // Store sound
        this.sounds.set(hotspotId, sound);

        logger.debug(`Spatial audio ready for ${hotspotId}`);

        // Handle autoplay if enabled
        if (config.audioAutoplay) {
          logger.debug(`Autoplay enabled for ${hotspotId}, attempting to play...`);
          try {
            sound.play();
            this.playingStates.set(hotspotId, true);
            // Update visual state for autoplay
            const material = this.materials.get(hotspotId);
            if (material && material instanceof ShaderMaterial) {
              material.setFloat("isPlaying", 1);
            }
            logger.info(`Autoplay started for ${hotspotId}`);
          } catch (playErr) {
            logger.debug(`Autoplay queued for ${hotspotId} - will play after user interaction`);
          }
        }

    } catch (e) {
        logger.error(`Error loading audio for ${config.id}:`, e);
    }
  }

  private isSoundStartingOrPlaying(sound: AbstractSound): boolean {
    return sound.activeInstancesCount > 0 ||
      sound.state === SoundState.Starting ||
      sound.state === SoundState.Started ||
      sound.state === SoundState.Paused;
  }

  /**
   * Play audio for a hotspot (AudioEngineV2 API)
   */
  public playAudio(id: string): void {
    const sound = this.sounds.get(id);

    if (!sound) {
      logger.warn(`No sound object found for ${id}`);
      return;
    }

    // Get the hotspot config for callbacks
    const hotspot = this.hotspots.get(id);
    const config = hotspot?.metadata?.config;

    // Track playing state manually since AudioEngineV2 sound objects
    // may not have a reliable isPlaying property
    const isCurrentlyPlaying = (this.playingStates.get(id) || false) || this.isSoundStartingOrPlaying(sound);

    // Toggle audio
    if (isCurrentlyPlaying) {
      try {
        sound.stop();
        this.playingStates.set(id, false);

        // Update isPlaying uniform
        const material = this.materials.get(id);
        if (material && material instanceof ShaderMaterial) {
          material.setFloat("isPlaying", 0);
        }
        logger.debug(`Stopped audio for hotspot ${id}`);

        // Trigger audio stop callback
        if (config && this.onAudioStop) {
          this.onAudioStop(config);
        }
      } catch (error) {
        logger.error('Error stopping audio:', error);
      }
    } else {
      try {
        logger.debug(`Starting playback for ${id}...`);
        logger.debug(`Sound object: ${sound}`);
        logger.debug(`AudioEngine state: ${this.audioEngine?.state}`);

        // Call play() - returns a sound instance for StaticSound
        const playResult = sound.play();
        logger.debug(`Play result: ${playResult}`);

        this.playingStates.set(id, true);

        // Update isPlaying uniform
        const material = this.materials.get(id);
        if (material && material instanceof ShaderMaterial) {
          material.setFloat("isPlaying", 1);
        }
        logger.info(`Playing audio for hotspot ${id}`);

        // Trigger audio play callback
        if (config && this.onAudioPlay) {
          this.onAudioPlay(config);
        }
      } catch (error) {
        logger.error(`Error playing audio ${id}:`, error);
        this.playingStates.set(id, false);

        // Reset the playing state on error
        const material = this.materials.get(id);
        if (material && material instanceof ShaderMaterial) {
          material.setFloat("isPlaying", 0);
        }
      }
    }
  }

  /**
   * Pause audio for a hotspot
   */
  public pauseAudio(id: string): void {
    const sound = this.sounds.get(id);
    const isPlaying = this.playingStates.get(id) || false;

    if (sound && isPlaying) {
      try {
        sound.pause();
        this.playingStates.set(id, false);

        // Update isPlaying uniform
        const material = this.materials.get(id);
        if (material && material instanceof ShaderMaterial) {
            material.setFloat("isPlaying", 0);
        }
        logger.debug(`Paused audio for hotspot ${id}`);
      } catch (error) {
        logger.error('Error pausing audio:', error);
      }
    }
  }

  /**
   * Check if audio is playing for a hotspot
   */
  public isAudioPlaying(id: string): boolean {
    return this.playingStates.get(id) || false;
  }

  /**
   * Get sound object for a hotspot (for progress tracking)
   */
  public getSound(id: string): AbstractSound | null {
    return this.sounds.get(id) ?? null;
  }

  /**
   * Stop audio for a hotspot
   */
  stopAudio(id: string): void {
    const sound = this.sounds.get(id);
    const isPlaying = this.playingStates.get(id) || false;

    if (sound && isPlaying) {
      try {
        sound.stop();
        this.playingStates.set(id, false);

        // Update isPlaying uniform on shader material
        const material = this.materials.get(id);
        if (material && material instanceof ShaderMaterial) {
          material.setFloat("isPlaying", 0);
        }

        logger.debug(`Stopped audio for hotspot ${id}`);
      } catch (error) {
        logger.error('Error stopping audio:', error);
      }
    }
  }

  /**
   * Stop all hotspot audio - FORCE stops ALL sounds regardless of tracked state
   * This is critical for preventing audio overlap during scene transitions
   */
  stopAllAudio(): void {
    logger.info(`Force stopping ALL ${this.sounds.size} hotspot audio sources`);

    this.sounds.forEach((sound, id) => {
      // Check if this sound was playing (for UI callback)
      const wasPlaying = this.playingStates.get(id) || false;

      // ALWAYS try to stop, regardless of playingStates - state can get out of sync
      if (sound) {
        try {
          sound.stop();
          logger.debug(`Stopped audio: ${id}`);
        } catch (error) {
          // Even if stop fails, continue to other sounds
          logger.warn(`Error stopping audio for ${id}:`, error);
        }
      }
      // Always reset state to false
      this.playingStates.set(id, false);

      // Also reset the shader uniform if material exists
      const material = this.materials.get(id);
      if (material && material instanceof ShaderMaterial) {
        try {
          material.setFloat("isPlaying", 0);
        } catch (_shaderErr) {
          // Ignore shader errors
        }
      }

      // CRITICAL: Trigger onAudioStop callback to update UI
      // Call for ANY sound that might have been playing, even if state was out of sync
      // This ensures the UI always gets reset during scene transitions
      if (this.onAudioStop) {
        const hotspot = this.hotspots.get(id);
        const config = hotspot?.metadata?.config;
        if (config && (config.type === 'audio' || config.type === 'both')) {
          logger.debug(`Triggering onAudioStop callback for ${id} (wasPlaying: ${wasPlaying})`);
          this.onAudioStop(config);
        }
      }
    });

    logger.info('All hotspot audio stopped');
  }

  /**
   * Update billboard mode for all hotspots based on VR state
   * Desktop: BILLBOARDMODE_ALL (full billboard)
   * VR: BILLBOARDMODE_Y (Y-axis only, stays upright)
   */
  setVRBillboardMode(isVR: boolean): void {
    const mode = isVR ? Mesh.BILLBOARDMODE_Y : Mesh.BILLBOARDMODE_ALL;
    logger.debug(`Setting billboard mode to ${isVR ? 'BILLBOARDMODE_Y (VR)' : 'BILLBOARDMODE_ALL (Desktop)'}`);

    this.hotspots.forEach((hotspot) => {
      // Only update plane-based hotspots (not navigation spheres)
      const config = hotspot.metadata?.config as HotspotConfig;
      if (config && config.type !== 'navigation') {
        hotspot.billboardMode = mode;
      }
    });

    // Also update any audio title text planes (they're children with billboardMode)
    this.scene.meshes.forEach(mesh => {
      if (mesh.name.startsWith('audio_title_') && mesh.billboardMode !== Mesh.BILLBOARDMODE_NONE) {
        mesh.billboardMode = mode;
      }
    });
  }

  /**
   * Parse hex color to Color3
   */
  private parseColor(hex: string): Color3 {
    // Remove # if present
    hex = hex.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    return new Color3(r, g, b);
  }

  /**
   * Set volume for all hotspot audio
   */
  setAllAudioVolume(volume: number): void {
    this.sounds.forEach((sound) => {
      sound.setVolume(volume);
    });
    logger.debug(`Set all hotspot audio volume to ${(volume * 100).toFixed(0)}%`);
  }

  /**
   * Get all hotspot meshes
   */
  getHotspots(): Map<string, Mesh> {
    return this.hotspots;
  }

  /**
   * Get a specific hotspot by ID
   */
  getHotspot(id: string): Mesh | undefined {
    return this.hotspots.get(id);
  }

  /**
   * Unload and dispose all hotspots
   * FIXED: Now properly removes renderObserver to prevent memory leaks
   */
  unload(): void {
    logger.debug(`Unloading ${this.hotspots.size} hotspot(s)...`);

    // Stop all audio
    this.stopAllAudio();

    // Dispose sounds
    this.sounds.forEach((sound) => {
      sound.dispose();
    });
    this.sounds.clear();

    // Dispose materials
    this.materials.forEach((material) => {
      material.dispose();
    });
    this.materials.clear();

    // Dispose analysers
    this.analysers.forEach((analyser) => {
      analyser.dispose();
    });
    this.analysers.clear();

    // FIXED: Clean up animation observables to prevent memory leaks
    this.animationObservables.forEach((obs) => {
      this.scene.onBeforeRenderObservable.remove(obs);
    });
    this.animationObservables.clear();

    // Dispose hotspot meshes
    this.hotspots.forEach((hotspot) => {
      if (hotspot.actionManager) {
        hotspot.actionManager.dispose();
      }
      hotspot.dispose();
    });
    this.hotspots.clear();

    // Clear animations
    this.animations.clear();

    // Clear playing states
    this.playingStates.clear();

    logger.info('All hotspots unloaded');
  }

  /**
   * Get debug info for AudioEngineV2 sounds
   */
  public getDebugInfo(): HotspotDebugInfo {
    const debugInfo: HotspotDebugInfo = {
      totalHotspots: this.hotspots.size,
      totalSounds: this.sounds.size,
      sounds: []
    };

    this.sounds.forEach((sound, id) => {
      const hotspot = this.hotspots.get(id);
      debugInfo.sounds.push({
        id: id,
        name: sound.name || id,
        isPlaying: this.playingStates.get(id) || false,
        volume: sound.volume,
        spatial: sound.spatial ? {
          enabled: true,
          distanceModel: sound.spatial.distanceModel,
          maxDistance: sound.spatial.maxDistance,
          rolloffFactor: sound.spatial.rolloffFactor,
          attachedTo: hotspot ? hotspot.name : 'Not attached'
        } : 'Not spatial',
        position: hotspot ? {
          x: hotspot.position.x.toFixed(2),
          y: hotspot.position.y.toFixed(2),
          z: hotspot.position.z.toFixed(2)
        } : null
      });
    });

    return debugInfo;
  }

  // =========================================================================
  // MODEL ATTACHMENT METHODS
  // =========================================================================

  /**
   * Attach a hotspot to a mesh, making it a child that follows the mesh's transform
   * @param hotspotId - ID of the hotspot to attach
   * @param parentMesh - The mesh to attach to
   * @param localOffset - Optional local offset from the mesh's position
   */
  attachToMesh(hotspotId: string, parentMesh: AbstractMesh, localOffset?: { x: number; y: number; z: number }): void {
    const hotspot = this.hotspots.get(hotspotId);
    if (!hotspot) {
      logger.warn(`Hotspot ${hotspotId} not found for mesh attachment`);
      return;
    }

    // Set parent
    hotspot.parent = parentMesh;

    // Apply local offset if provided
    if (localOffset) {
      hotspot.position = new Vector3(localOffset.x, localOffset.y, localOffset.z);
    } else {
      // Reset position to parent origin
      hotspot.position = Vector3.Zero();
    }

    // Store attachment info in metadata
    hotspot.metadata = {
      ...hotspot.metadata,
      attachedToMesh: parentMesh.name,
      localOffset: localOffset || { x: 0, y: 0, z: 0 },
    };

    logger.debug(`Hotspot ${hotspotId} attached to mesh ${parentMesh.name}`);
  }

  /**
   * Attach a hotspot to a skeleton bone (for animated models)
   * @param hotspotId - ID of the hotspot to attach
   * @param skeleton - The skeleton containing the bone
   * @param boneName - Name of the bone to attach to
   * @param mesh - The mesh the skeleton is attached to (needed for bone attachment)
   * @param localOffset - Optional local offset from the bone's position
   */
  attachToBone(
    hotspotId: string,
    skeleton: Skeleton,
    boneName: string,
    mesh: AbstractMesh,
    localOffset?: { x: number; y: number; z: number }
  ): void {
    const hotspot = this.hotspots.get(hotspotId);
    if (!hotspot) {
      logger.warn(`Hotspot ${hotspotId} not found for bone attachment`);
      return;
    }

    // Find the bone
    const bone = skeleton.bones.find((b: Bone) => b.name === boneName);
    if (!bone) {
      logger.warn(`Bone ${boneName} not found in skeleton for hotspot ${hotspotId}`);
      return;
    }

    // Attach to bone
    hotspot.attachToBone(bone, mesh);

    // Apply local offset if provided
    if (localOffset) {
      hotspot.position = new Vector3(localOffset.x, localOffset.y, localOffset.z);
    } else {
      hotspot.position = Vector3.Zero();
    }

    // Store attachment info in metadata
    hotspot.metadata = {
      ...hotspot.metadata,
      attachedToBone: boneName,
      attachedToSkeleton: skeleton.name,
      localOffset: localOffset || { x: 0, y: 0, z: 0 },
    };

    logger.debug(`Hotspot ${hotspotId} attached to bone ${boneName}`);
  }

  /**
   * Detach a hotspot from its parent mesh or bone
   * Restores the hotspot to world position
   * @param hotspotId - ID of the hotspot to detach
   */
  detachFromParent(hotspotId: string): void {
    const hotspot = this.hotspots.get(hotspotId);
    if (!hotspot) {
      logger.warn(`Hotspot ${hotspotId} not found for detachment`);
      return;
    }

    // Get world position before detaching
    const worldPosition = hotspot.getAbsolutePosition();

    // Remove parent
    hotspot.parent = null;
    hotspot.detachFromBone();

    // Set world position
    hotspot.position = worldPosition.clone();

    // Clear attachment metadata
    if (hotspot.metadata) {
      delete hotspot.metadata.attachedToMesh;
      delete hotspot.metadata.attachedToBone;
      delete hotspot.metadata.attachedToSkeleton;
      delete hotspot.metadata.localOffset;
    }

    logger.debug(`Hotspot ${hotspotId} detached from parent`);
  }

  /**
   * Get attachment info for a hotspot
   * @param hotspotId - ID of the hotspot
   */
  getAttachmentInfo(hotspotId: string): {
    attachedToMesh?: string;
    attachedToBone?: string;
    attachedToSkeleton?: string;
    localOffset?: { x: number; y: number; z: number };
  } | null {
    const hotspot = this.hotspots.get(hotspotId);
    if (!hotspot || !hotspot.metadata) {
      return null;
    }

    const { attachedToMesh, attachedToBone, attachedToSkeleton, localOffset } = hotspot.metadata;
    if (!attachedToMesh && !attachedToBone) {
      return null;
    }

    return {
      attachedToMesh,
      attachedToBone,
      attachedToSkeleton,
      localOffset,
    };
  }

  /**
   * Dispose component resources
   */
  dispose(): void {
    this.unload();
    if (this.renderObserver) {
      this.scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = null;
    }
  }

  /**
   * Update shader uniforms every frame
   */
  private updateShaders(): void {
    const time = performance.now() / 1000;

    this.materials.forEach((material, id) => {
      if (material instanceof ShaderMaterial) {
        material.setFloat("time", time);

        // Update audio level if applicable
        if (this.analysers.has(id)) {
          const analyser = this.analysers.get(id);
          if (analyser) {
            const frequencies = analyser.getByteFrequencyData();
            // Get average level from low-mid frequencies
            let sum = 0;
            const count = Math.min(frequencies.length, 8); // Use first 8 bins
            for (let i = 0; i < count; i++) {
              sum += frequencies[i];
            }
            const average = sum / count / 255.0; // Normalize 0-1

            // Smooth falloff could be added here, but analyser smoothing helps
            material.setFloat("audioLevel", average);
          }
        }
      }
    });

    // Spatial audio is now handled automatically by AudioEngineV2!
    // No manual position updates needed - sound.spatial.attach() does it for us
  }
}
